/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/require-tagless-components, no-unused-vars */
/* global mapkit */
// Ember imports
import Component from '@ember/component';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
// External imports
import config from '@linc-technologies/linced-common/utils/config';

export default class MapkitjsMap extends Component {
  // Services
  @service
  fetch;

  @service
  paperToaster;

  // Attributes
  // map is the initiated mapkitjs object
  @tracked
  map = null;

  // geocoder is the mapkitjs's geocoder object for translating coordinates
  @tracked
  geocoder = null;

  // annotationTitle is the title displayed under the annotation pin
  annotationTitle = '';

  address1 = '';
  address2 = '';

  // coordinates is an object containing longitude and latitude properties
  coordinates = null;

  // Hooks
  didRender() {
    super.didRender(...arguments);
    this.intializeMapkit();
  }

  // Functions
  // initializeMapkit calls the mapkit's init() method and initializes the mapkit
  // using the generated token from the auth service
  intializeMapkit() {
    mapkit.init({
      authorizationCallback: (done) => {
        let uri = `${config.APP.apiHost}/auth/v4/mapkit/:generateToken`;
        let data = {
          teamId: config.apple.teamId,
          keyId: config.apple.keyId,
        };
        let params = {
          data: JSON.stringify(data),
        };
        // Send a request to generate a token to initialize maps
        this.fetch
          .post(uri, params)
          .then((res) => {
            // Finally, use the token as the callback
            done(res.token);
          })
          .catch(() => {
            this.paperToaster.show(`Error displaying a map.`, {
              duration: 4000,
              position: 'top right',
            });
          });
      },
    });
    // create a geocoder object so we can translate the coordinates
    let geocoder = new mapkit.Geocoder();
    this.geocoder = geocoder;
    this.handleCoordinates().then(() => this.insertTheMapContainer());
  }

  // handleCoordinates asynchroniously gets the coordinates
  async handleCoordinates() {
    try {
      await this.getCoordinates();
    } catch (error) {
      this.paperToaster.show(`Error displaying a map.`, {
        duration: 4000,
        position: 'top right',
      });
    }
  }

  // getCoordinates translates the two passed in addresses
  // into latitude and longitude coordinates
  async getCoordinates() {
    if (!isEmpty(this.geocoder)) {
      // Geocoder is initiated, can lookup coordinates
      return new Promise((resolve, reject) => {
        this.geocoder.lookup(
          `${this.address1}, ${this.address2}`,
          (err, data) => {
            if (err) {
              reject(err);
            } else {
              let results = data.results;
              if (results.length > 0) {
                // Extract the coordinates from the lookup result
                this.coordinates = results[0].coordinate;
                resolve(this.coordinates);
              } else {
                // Had an oopsie - reject the promise and stop from inserting the map further
                reject(new Error('No coordinates found'));
              }
            }
          },
        );
      });
    }
  }

  // insertTheMapContainer inserts a div container into the component's parent container
  // after the successful retrieval of coordinates
  // the mapContainer is of fixed height, and having it in the DOM without the actual map
  // will look weird
  insertTheMapContainer() {
    let div = document.createElement('div');
    div.id = 'mapContainer';
    let containerElement = this.element;
    containerElement.appendChild(div);

    this.insertTheMap();
  }

  // insertTheMap actually inserts the map with everything we've setup
  insertTheMap() {
    if (!isEmpty(document.getElementById('mapContainer'))) {
      // center is the main coords of the school
      let center = new mapkit.Coordinate(
          this.coordinates.latitude,
          this.coordinates.longitude,
        ),
        span = new mapkit.CoordinateSpan(0.0125, 0.0125),
        region = new mapkit.CoordinateRegion(center, span);

      let annotation = new mapkit.MarkerAnnotation(center, {
        title: this.annotationTitle,
        color: '#d51f27',
        displayPriority: 2000,
        animates: false,
      });
      let map = new mapkit.Map('mapContainer', {
        showsCompass: mapkit.FeatureVisibility.Hidden,
        region: region,
      });
      this.map = map;
      map.addAnnotation(annotation);
    }
  }
}
